import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { liveUpdateService, getVersionJsonUrl, VersionInfo } from '../../services/liveUpdateService';
import Logo from './Logo';

const DEFAULT_VERSION: VersionInfo = {
  version: (import.meta as any).env?.VITE_APP_VERSION || '0.0.0',
  buildNumber: 1,
  buildHash: 'dev',
  buildDate: new Date().toISOString(),
};

const SplashScreen: React.FC = () => {
  const { t } = useTranslation();
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);

  // Affichage de la version uniquement. La vérification OTA est centralisée dans liveUpdateService.
  useEffect(() => {
    let cancelled = false;

    const loadVersion = async () => {
      const local = liveUpdateService.getCurrentVersion();
      if (local?.buildNumber) {
        if (!cancelled) setCurrentVersion(local);
        return;
      }

      try {
        const base = getVersionJsonUrl();
        const res = await fetch(`${base}?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
          signal: AbortSignal.timeout(3000),
        });
        if (res.ok && !cancelled) {
          const data = (await res.json()) as VersionInfo;
          if (data?.buildNumber) {
            setCurrentVersion(data);
            localStorage.setItem('app_version', JSON.stringify(data));
            return;
          }
        }
      } catch {
        // ignore
      }
      if (!cancelled) setCurrentVersion(DEFAULT_VERSION);
    };

    loadVersion();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayVersion = currentVersion ?? DEFAULT_VERSION;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="flex flex-col items-center animate-fade-in">
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-primary-500 rounded-full blur-xl opacity-20 animate-pulse"></div>
          <div className="relative z-10">
            <Logo location="splash" size="xlarge" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-12 font-medium">
          {t('SplashScreen.tagline.slogan')}
        </p>
        <div className="flex items-center space-x-2">
          <div className="w-2.5 h-2.5 bg-primary-500 rounded-full animate-bounce [animation-delay:0s]"></div>
          <div className="w-2.5 h-2.5 bg-primary-500 rounded-full animate-bounce [animation-delay:0.1s]"></div>
          <div className="w-2.5 h-2.5 bg-primary-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2">
        <div className="text-xs text-gray-400 dark:text-gray-600 font-light">
          {t('SplashScreen.status.loading')}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-full border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {t('SplashScreen.footer.buildLabel')}
          </span>
          <span className="text-xs font-black text-primary-600 dark:text-primary-400 font-mono">
            #{displayVersion.buildNumber}
          </span>
          {displayVersion.version && (
            <>
              <span className="text-[8px] text-gray-300 dark:text-gray-600">•</span>
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                v{displayVersion.version}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
